interface BarCacheEntry {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface CachedBars {
  bars: BarCacheEntry[];
  timestamp: number;
  /**
   * Whether the cached bars were retrieved via the 1m fallback (dollar empty).
   * Persisted so a cache-paint on symbol/timeframe switch can restore the
   * correct 1m-proxy badge synchronously — otherwise the previous selection's
   * fallback marker lingers until the async refetch resolves (stale badge).
   */
  isFallback: boolean;
}

const CACHE_PREFIX = 'tm:bars:';
const MAX_CACHE_AGE_MS = 5 * 60 * 1000;

export function saveBarCache(
  symbol: string,
  timeframe: string,
  bars: BarCacheEntry[],
  isFallback = false,
): void {
  const key = `${CACHE_PREFIX}${symbol}:${timeframe}`;
  const data: CachedBars = { bars: bars.slice(-2000), timestamp: Date.now(), isFallback };
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // quota exceeded — silently ignore
  }
}

/** Full cache entry (bars + fallback marker) or null if missing/stale. */
export function loadBarCacheEntry(
  symbol: string,
  timeframe: string,
): { bars: BarCacheEntry[]; isFallback: boolean } | null {
  const key = `${CACHE_PREFIX}${symbol}:${timeframe}`;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data: CachedBars = JSON.parse(raw);
    if (Date.now() - data.timestamp > MAX_CACHE_AGE_MS) return null;
    return { bars: data.bars, isFallback: data.isFallback ?? false };
  } catch {
    return null;
  }
}

export function loadBarCache(symbol: string, timeframe: string): BarCacheEntry[] | null {
  return loadBarCacheEntry(symbol, timeframe)?.bars ?? null;
}

export function barLimit(tf: string): number {
  switch (tf) {
    case 'dollar':
    case 'volume':
      return 500;
    case '1m':
      return 1440;
    case '5m':
    case '15m':
    case '30m':
    case '1H':
    case '2H':
      return 500;
    case '4H':
      return 500;
    case '1D':
      return 365;
    default:
      return 500;
  }
}
